import MissionPartnerGuidanceCardView from './MissionPartnerGuidanceCardView';
import { guidanceDisplayText } from './guidanceDisplay';
import React from 'react';
import Alert from '@mui/material/Alert';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';

import SimilarRequirementsResults from '../SimilarRequirements/SimilarRequirementsResults';
import type {
  HomeChatCard,
  SimilarRequirementsResultCard,
  SimilarRequirementsTypeChooserCard,
  AmbiguousIntentChoiceCard,
  WorkflowRecommendationChoiceCard,
  ActionChoiceCard,
  PurchaseGuidanceCard,
  MissionPartnerGuidanceCard,
} from './types';

type Props = {
  cards: HomeChatCard[];
  busyCardId: string | null;
  onSelectType: (card: SimilarRequirementsTypeChooserCard, selectionKey: string) => void;
  onCancel: (cardId: string) => void;
  onOpen: (cardId: string, openHandle: string) => void;
  onSelectIntentChoice: (card: AmbiguousIntentChoiceCard, selectionKey: string) => void;
  onSelectWorkflowRecommendation: (card: WorkflowRecommendationChoiceCard, selectionKey: string) => void;
  onSelectActionChoice: (card: ActionChoiceCard, selectionKey: string) => void;
  onSelectMissionPartnerGuidance?: (card: MissionPartnerGuidanceCard, selectionKey: string) => void;
  onSelectPurchaseGuidance?: (card: PurchaseGuidanceCard, selectionKey: string) => void;
  focusNewPurchaseGuidanceCardId?: string | null;
  onPurchaseGuidanceHeadingFocused?: (cardId: string) => void;
  onOpenCreatedWorkflow?: (card: WorkflowRecommendationChoiceCard | ActionChoiceCard) => void;
  workflowRecommendationBusy?: boolean;
  actionChoicePendingLabel?: string | null;
};

const cardSx = {
  mx: 2,
  mb: 2,
  p: 2,
  borderColor: 'divider',
  borderRadius: 2,
};

// Recommendation labels are server-authored selection tokens. Adapt only
// their presentation so routing continues to use the original opaque key.
const displayWorkflowChoice = (label: string): string => label === 'Construction Price Estimator'
  ? 'Price realism estimator' : label;
const LEGACY_CONSTRUCTION_VEHICLE_QUESTION = 'Are you referring to the contract vehicle, or a specific product?';

function clarificationQuestion(card: PurchaseGuidanceCard): string {
  if (card.message) return card.message;
  // Older durable SABER/MACC cards predate the single-question field. Recognize
  // only the reviewed vehicle label or the exact legacy ambiguity sentence;
  // never build a question by echoing arbitrary saved card text.
  const isReviewedVehicle = /^(SABER|MACC)$/i.test(card.program_name || '');
  const isLegacyAmbiguity = /^BLUF: I need to confirm whether (SABER|MACC) means the construction contract vehicle or a product named \1\.$/i.test(card.bluf);
  if (isReviewedVehicle || isLegacyAmbiguity) return LEGACY_CONSTRUCTION_VEHICLE_QUESTION;
  return card.support_text || card.bluf;
}

function PurchaseGuidanceCardView({ card, busy, onSelect, focusHeading, onHeadingFocused }: {
  card: PurchaseGuidanceCard;
  busy: boolean;
  onSelect?: Props['onSelectPurchaseGuidance'];
  focusHeading?: boolean;
  onHeadingFocused?: Props['onPurchaseGuidanceHeadingFocused'];
}) {
  const display = guidanceDisplayText();
  const savedBluf = /^BLUF:\s*$/i.test(card.bluf) ? `BLUF: ${card.steps[0]?.text || card.reason}` : card.bluf;
  const bluf = display(savedBluf);
  const question = card.variant === 'clarification' ? display(clarificationQuestion(card)) : '';
  const displayedSteps = card.steps.map(step => ({ ...step, text: display(step.text) }));
  const pending = busy || card.state === 'pending' || card.state === 'checking';
  const hasActions = card.state === 'ready' || card.state === 'pending';
  const isProhibitedHold = card.state === 'hold' && card.variant === 'prohibited';
  const headingRef = React.useRef<HTMLHeadingElement>(null);
  const receiptRef = React.useRef<HTMLDivElement>(null);
  const previousState = React.useRef(card.state);
  const headingFocused = React.useRef(false);
  const stepsContent = <Box component="ol" sx={{ pl: 3, mt: 0.5, '& li': { mb: 1 } }}>
    {displayedSteps.map((step) => <li key={step.id}><Typography variant="body2">{step.text}</Typography></li>)}
  </Box>;
  // Older saved cards may contain catalog and vehicle-identification evidence.
  // The answer cites one actual RFO part; supporting evidence stays in the steps.
  const rfoPartLink = card.policy_basis.links.find((link) =>
    /^https:\/\/www\.acquisition\.gov\/far-overhaul\/far-part-deviation-guide\/far-overhaul-part-\d+\/?$/.test(link.url)
  );
  React.useEffect(() => {
    if (focusHeading && !headingFocused.current && card.state !== 'selected') {
      headingRef.current?.focus();
      headingFocused.current = true;
      onHeadingFocused?.(card.card_id);
    }
  }, [card.card_id, card.state, focusHeading, onHeadingFocused]);
  React.useEffect(() => {
    if (card.state === 'selected' && previousState.current !== 'selected') {
      receiptRef.current?.focus();
    }
    previousState.current = card.state;
  }, [card.state]);
  // A question is a normal assistant turn. The routing facts are intentionally
  // hidden until the user clarifies which meaning of the term they intended.
  if (card.variant === 'clarification' && card.state === 'support') {
    return <Paper elevation={0} data-testid="purchase-guidance-card" data-role="assistant" aria-live="polite"
      sx={{ alignSelf: 'flex-start', maxWidth: '85%', px: 1.5, py: 1, borderRadius: 2,
        bgcolor: 'surfaceMuted', color: 'text.primary', boxShadow: 1, overflowWrap: 'anywhere' }}>
      <Typography component="h3" variant="subtitle1" tabIndex={-1} ref={headingRef} sx={{ fontWeight: 700 }}>{bluf}</Typography>
      <Typography variant="body2" sx={{ mt: 1 }}>{question !== bluf ? question : 'What product, service, or construction work do you need?'}</Typography>
    </Paper>;
  }
  return (
    <Paper
      elevation={0}
      sx={{ alignSelf: 'flex-start', maxWidth: '85%', px: 1.5, py: 1, borderRadius: 2,
        bgcolor: 'surfaceMuted', color: 'text.primary', boxShadow: 1, overflowWrap: 'anywhere' }}
      data-testid="purchase-guidance-card" data-role="assistant" aria-busy={pending} aria-live="polite"
    >
      <Typography component="h3" variant="subtitle1" tabIndex={-1} ref={headingRef} sx={{ fontWeight: 700 }}>
        {bluf}
      </Typography>
      {card.steps.length > 0 && <Box component="section" aria-label={isProhibitedHold ? 'What to do next' : 'How this purchase works'} sx={{ mt: 1.5 }}>
        <Typography component="h4" variant="subtitle2" sx={{ fontWeight: 700 }}>{isProhibitedHold ? 'What to do next' : 'How this purchase works'}</Typography>
        {stepsContent}
      </Box>}
      {rfoPartLink && <Box component="section" aria-label="Policy basis" sx={{ mt: 1 }}>
        <Typography component="h4" variant="subtitle2" sx={{ fontWeight: 700 }}>Policy basis</Typography>
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          <a href={rfoPartLink.url} target="_blank" rel="noopener noreferrer">{rfoPartLink.label}</a>
        </Typography>
      </Box>}
      {card.warning && <Alert icon={<WarningAmberIcon />} severity="warning" sx={{ mt: 1.5 }}>{card.warning}</Alert>}
      {card.support_text && card.state !== 'error' && <Alert severity="info" sx={{ mt: 1.5 }}>{card.support_text}</Alert>}
      {hasActions && card.options.length > 0 && card.variant !== 'clarification' && <Box sx={{ display: 'grid', gap: 1, mt: 1.5, width: '100%' }}>
        {card.options.map((option) => <Button
          key={option.selection_key} fullWidth variant="contained" disabled={pending || !onSelect}
          onClick={() => onSelect?.(card, option.selection_key)}
          sx={{ minHeight: 44, textAlign: 'left', justifyContent: 'flex-start', whiteSpace: 'normal' }}
        >
          <Box><Typography component="span" display="block" sx={{ fontWeight: 700 }}>{option.label}</Typography>
            {option.description && !/^(Start|Open) Market Research$/i.test(option.label.trim()) &&
              <Typography component="span" display="block" variant="body2">{option.description}</Typography>}
          </Box>
        </Button>)}
      </Box>}
      {pending && <Box role="status" sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
        <CircularProgress size={20} aria-label="Checking purchase route" />
        <Typography variant="body2">{card.pending_text || 'Checking approved source…'}</Typography>
      </Box>}
      {card.state === 'selected' && <Alert severity="success" sx={{ mt: 1.5 }} tabIndex={-1} ref={receiptRef}>{card.receipt}</Alert>}
      {card.state === 'error' && <Alert severity="error" sx={{ mt: 1.5 }}>{card.support_text || 'This route is temporarily unavailable. Contact Contracting for help.'}</Alert>}
    </Paper>
  );
}

function TypeChooserCard({ card, busy, onSelectType, onCancel }: {
  card: SimilarRequirementsTypeChooserCard;
  busy: boolean;
  onSelectType: Props['onSelectType'];
  onCancel: Props['onCancel'];
}) {
  const unavailable = card.state === 'cancelled' || card.state === 'selected';
  return (
    <Paper variant="outlined" sx={cardSx} data-testid="similar-requirements-type-chooser" aria-live="polite">
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        {card.prompt || 'Choose the type of procurement to search.'}
      </Typography>
      {card.error && <Alert severity="warning" sx={{ mt: 1 }}>{card.error}</Alert>}
      {!unavailable && (
        <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
          {card.options.map((option, index) => (
            <Button
              key={option.selection_key}
              variant={index === 0 ? 'contained' : 'outlined'}
              onClick={() => onSelectType(card, option.selection_key)}
              disabled={busy}
              autoFocus={index === 0}
            >
              {option.label}
            </Button>
          ))}
        </Box>
      )}
      <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
        {!unavailable && <Button onClick={() => onCancel(card.card_id)} disabled={busy}>Cancel</Button>}
        {busy && <CircularProgress size={20} aria-label="Updating similar requirements search" />}
      </Box>
    </Paper>
  );
}

function CreatedWorkflowControl({ card, busy, onOpen }: {
  card: WorkflowRecommendationChoiceCard | ActionChoiceCard;
  busy: boolean;
  onOpen: Props['onOpenCreatedWorkflow'];
}) {
  if (card.created_workflow?.status === 'unavailable') {
    return <Alert severity="warning" sx={{ mt: 1.5 }}>This workflow is no longer available.</Alert>;
  }
  return <Button variant="contained" sx={{ mt: 1.5 }} disabled={busy || !onOpen} onClick={() => onOpen?.(card)}>Open workflow</Button>;
}

function WorkflowRecommendationCard({ card, busy, onSelect, onOpen }: {
  card: WorkflowRecommendationChoiceCard;
  busy: boolean;
  onSelect: Props['onSelectWorkflowRecommendation'];
  onOpen: Props['onOpenCreatedWorkflow'];
}) {
  const options = card.state === 'selected'
    ? card.options.filter((option) => option.label === card.selected_label)
    : card.options;
  const unavailable = !!card.created_workflow || card.state === 'error';
  const safeUnavailableMessage = card.state === 'error'
    ? card.error || 'That choice is no longer available.'
    : card.error;
  const selectionReceipt = card.state === 'selected'
    ? card.selected_label
      ? `You opened ${displayWorkflowChoice(card.selected_label)}.`
      : 'You opened the recommended workflow.'
    : null;
  return (
    <Paper
      variant="outlined"
      sx={{ ...cardSx, borderLeft: 4, borderLeftColor: 'primary.main' }}
      data-testid="workflow-recommendation-choice"
      aria-live="polite"
      aria-busy={busy}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{card.prompt}</Typography>
      {safeUnavailableMessage && <Alert severity="warning" sx={{ mt: 1 }}>{safeUnavailableMessage}</Alert>}
      {selectionReceipt && (
        <Alert severity="success" sx={{ mt: 1.5 }} data-testid="workflow-recommendation-selection-receipt">
          {selectionReceipt}
        </Alert>
      )}
      {!unavailable && (
        <Box sx={{ display: 'grid', gap: 1, mt: 1.5 }}>
          {options.map((option) => {
            const descriptionId = `${card.card_id}-${option.selection_key}-description`;
            return (
              <Button
                key={option.selection_key}
                fullWidth
                variant="outlined"
                onClick={() => onSelect(card, option.selection_key)}
                disabled={busy}
                aria-label={displayWorkflowChoice(option.label)}
                aria-describedby={descriptionId}
                sx={{ alignItems: 'flex-start', justifyContent: 'flex-start', textAlign: 'left', px: 1.5, py: 1.25 }}
              >
                <Box>
                  <Typography component="span" display="block" sx={{ fontWeight: 700 }}>{displayWorkflowChoice(option.label)}</Typography>
                  <Typography id={descriptionId} component="span" display="block" variant="body2" color="text.secondary" sx={{ mt: 0.25, textTransform: 'none' }}>
                    {option.description}
                  </Typography>
                </Box>
              </Button>
            );
          })}
        </Box>
      )}
      {card.created_workflow && <CreatedWorkflowControl card={card} busy={busy} onOpen={onOpen} />}
      {busy && <Box role="status" sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1.5 }}><CircularProgress size={20} aria-label="Starting workflow" /><Typography variant="body2">Opening workflow…</Typography></Box>}
    </Paper>
  );
}

function ActionChoiceCardView({ card, busy, pendingLabel, onSelect, onOpen }: {
  card: ActionChoiceCard;
  busy: boolean;
  pendingLabel?: string | null;
  onSelect: Props['onSelectActionChoice'];
  onOpen: Props['onOpenCreatedWorkflow'];
}) {
  const unavailableCopy = card.error || 'This option is no longer available for this question. Ask this question again, or ask a new question.';
  // The selection key is opaque and the private action never reaches the
  // browser.  Prefer the explicit public presentation marker; label/key
  // checks preserve saved cards from before that marker existed.
  const isBackNavigation = (option: ActionChoiceCard['options'][number]) => (
    option.presentation === 'back'
    || option.label.trim().toLocaleLowerCase() === 'back'
    || option.selection_key.trim().toLocaleLowerCase() === 'back'
  );
  const backOption = card.options.find(isBackNavigation);
  const choiceOptions = card.options.filter((option) => !isBackNavigation(option));
  return <Paper variant="outlined" sx={{ ...cardSx, borderLeft: 4, borderLeftColor: 'primary.main' }} data-testid="action-choice" aria-live="polite" aria-busy={busy}>
    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{card.prompt}</Typography>
    {card.state === 'selected' && <Alert severity="success" sx={{ mt: 1.5 }}>{card.selected_label ? `You opened ${displayWorkflowChoice(card.selected_label)}.` : 'Your selection was opened.'}</Alert>}
    {card.state === 'error' && <Alert severity="warning" sx={{ mt: 1 }}>{unavailableCopy}</Alert>}
    {!card.created_workflow && !busy && choiceOptions.length > 0 && <Box data-testid="action-choice-options" sx={{ display: 'grid', gap: 1, mt: 1.5 }}>{choiceOptions.map((option) => (
      <Button key={option.selection_key} data-testid={`action-choice-option-${option.selection_key}`} fullWidth variant="outlined" disabled={busy} onClick={() => onSelect(card, option.selection_key)} sx={{ alignItems: 'flex-start', justifyContent: 'flex-start', textAlign: 'left', px: 1.5, py: 1.25 }}>
        <Box><Typography component="span" display="block" sx={{ fontWeight: 700 }}>{card.state === 'error' ? `Retry: ${displayWorkflowChoice(option.label)}` : displayWorkflowChoice(option.label)}</Typography>{option.description && <Typography component="span" display="block" variant="body2" color="text.secondary" sx={{ mt: 0.25, textTransform: 'none' }}>{option.description}</Typography>}</Box>
      </Button>
    ))}</Box>}
    {!card.created_workflow && !busy && backOption && <Box data-testid="action-choice-back-control" sx={{ display: 'flex', justifyContent: 'flex-start', mt: 1.5 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        variant="text"
        disabled={busy}
        onClick={() => onSelect(card, backOption.selection_key)}
        aria-label="Back to document type options"
      >
        Back
      </Button>
    </Box>}
    {card.created_workflow && <CreatedWorkflowControl card={card} busy={busy} onOpen={onOpen} />}
    {busy && (
      <Box data-testid="action-choice-pending" role="status" sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1.5 }}>
        <CircularProgress size={20} aria-label={pendingLabel ? displayWorkflowChoice(pendingLabel) : 'Opening selected action'} />
        <Typography variant="body2">{pendingLabel ? displayWorkflowChoice(pendingLabel) : 'Opening your selection…'}</Typography>
      </Box>
    )}
  </Paper>;
}

function ResultCard({ card, busy, onOpen }: {
  card: SimilarRequirementsResultCard;
  busy: boolean;
  onOpen: Props['onOpen'];
}) {
  const unavailable = card.results.some((result) => 'status' in result);
  const rows = card.results.flatMap((result) => (
    'status' in result
      ? []
      : [{
        // Display identity is not an open capability. Foreign summaries remain
        // visible without receiving a workflow ID or an open handle.
        rowKey: result.result_key,
        openValue: result.can_open ? result.open_handle : undefined,
        workflowName: result.workflow_name,
        ownerEmail: result.owner_email,
        ownerUnit: result.owner_unit,
        updatedAt: result.updated_at,
        lastUpdatedDisplay: result.last_updated_display,
        canOpen: result.can_open && !!result.open_handle,
        readOnly: result.read_only,
      }]
  ));
  const hasResults = rows.length > 0;
  const resultHeading = card.state === 'complete' && !card.error
    ? hasResults
      ? 'Here are similar requirements'
      : unavailable
        ? null
        : 'No similar requirements found'
    : null;
  return (
    <Paper variant="outlined" sx={cardSx} data-testid="similar-requirements-result-card" aria-live="polite">
      {resultHeading && <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{resultHeading}</Typography>}
      <Box sx={{ mt: 1.5 }}>
        <SimilarRequirementsResults
          loading={card.state === 'ready' && !card.error}
          error={card.error}
          rows={rows}
          unavailable={unavailable}
          onOpen={(row) => {
            if (row.canOpen && row.openValue) onOpen(card.card_id, row.openValue);
          }}
        />
      </Box>
      {busy && <CircularProgress sx={{ mt: 1.5 }} size={20} aria-label="Updating similar requirements search" />}
    </Paper>
  );
}

function AmbiguousIntentChoiceCard({ card, busy, onSelect }: {
  card: AmbiguousIntentChoiceCard;
  busy: boolean;
  onSelect: Props['onSelectIntentChoice'];
}) {
  const unavailable = card.state !== 'pending';
  const safeUnavailableMessage = card.state === 'error'
    ? card.error || 'That choice is no longer available. Please start a new chat or send another message.'
    : card.error;
  return (
    <Paper variant="outlined" sx={cardSx} data-testid="ambiguous-intent-choice" aria-live="polite">
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{card.prompt}</Typography>
      {safeUnavailableMessage && <Alert severity="warning" sx={{ mt: 1 }}>{safeUnavailableMessage}</Alert>}
      {!unavailable && <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap', alignItems: 'stretch' }}>
        {card.options.map((option, index) => {
          // The server-issued label remains bound to the opaque selection key,
          // while workflow choices use their formal names as their sole visible
          // button label. No secondary qualifier is rendered below a button.
          const presentation = option.label === 'Research products and suppliers — Commodities Market Research'
            || option.label === 'Commodities Market Research'
            ? 'Commodities Market Research'
            : option.label === 'Buy with a purchase card — GPC'
              || option.label === 'Buy with a Government Purchase Card'
              || option.label === 'Start a GPC purchase'
              ? 'Buy with a Government Purchase Card'
              : option.label;
          return (
            <Box key={option.selection_key} data-testid={`ambiguous-intent-choice-option-${index}`} data-server-label={option.label} sx={{ minWidth: 196 }}>
              <Button variant={index === 0 ? 'contained' : 'outlined'} onClick={() => onSelect(card, option.selection_key)} disabled={busy} autoFocus={index === 0}>
                {presentation}
              </Button>
            </Box>
          );
        })}
      </Box>}
      {busy && <CircularProgress sx={{ mt: 1.5 }} size={20} aria-label="Choosing Home Chat action" />}
    </Paper>
  );
}

const SimilarRequirementsCards: React.FC<Props> = (props) => (
  <>
    {props.cards.map((card) => {
      const busy = props.busyCardId === card.card_id;
      if (card.kind === 'similar_requirements_type_chooser') {
        return <TypeChooserCard key={card.card_id} card={card} busy={busy} onSelectType={props.onSelectType} onCancel={props.onCancel} />;
      }
      if (card.kind === 'similar_requirements_results') {
        return <ResultCard key={card.card_id} card={card} busy={busy} onOpen={props.onOpen} />;
      }
      if (card.kind === 'ambiguous_intent_choice') {
        return <AmbiguousIntentChoiceCard key={card.card_id} card={card} busy={busy} onSelect={props.onSelectIntentChoice} />;
      }
      if (card.kind === 'workflow_recommendation_choice') {
        return <WorkflowRecommendationCard key={card.card_id} card={card} busy={busy || !!props.workflowRecommendationBusy} onSelect={props.onSelectWorkflowRecommendation} onOpen={props.onOpenCreatedWorkflow} />;
      }
      if (card.kind === 'action_choice') {
        return <ActionChoiceCardView key={card.card_id} card={card} busy={busy} pendingLabel={busy ? props.actionChoicePendingLabel : null} onSelect={props.onSelectActionChoice} onOpen={props.onOpenCreatedWorkflow} />;
      }
      if (card.kind === 'mission_partner_guidance') {
        return <MissionPartnerGuidanceCardView key={card.card_id} card={card} busy={busy} onSelect={props.onSelectMissionPartnerGuidance} />;
      }
      if (card.kind === 'purchase_guidance') {
        return <PurchaseGuidanceCardView key={card.card_id} card={card} busy={busy} onSelect={props.onSelectPurchaseGuidance}
          focusHeading={props.focusNewPurchaseGuidanceCardId === card.card_id}
          onHeadingFocused={props.onPurchaseGuidanceHeadingFocused} />;
      }
      return (
        <Paper key={card.card_id} variant="outlined" sx={cardSx} data-testid="similar-requirements-clarification-card" aria-live="polite">
          <Typography variant="body2">{card.prompt}</Typography>
        </Paper>
      );
    })}
  </>
);

export default SimilarRequirementsCards;
